import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { fmtDateTime, money, reconciliationModeLabel } from "@/lib/format";

interface ConnectorRow {
  id: string;
  name: string;
  provider: string | null;
  enabled: boolean;
}

interface ReconciliationRow {
  id: string;
  listing_id: string;
  connector_id: string | null;
  mode: string;
  status: string;
  last_run_at: string | null;
  drift_domains: string[] | null;
  drift_details: Record<string, unknown> | null;
  action_taken: string | null;
  error_message: string | null;
  created_at: string;
}

interface ListingRecap {
  connectorId: string;
  connectorName: string;
  provider: string;
  listingId: string;
  listingTitle: string | null;
  mode: string;
  status: string;
  driftDomains: string[];
  driftDetails: Record<string, unknown> | null;
  action_taken: string | null;
  lastRun: string | null;
}

interface ProviderGroup {
  connectorId: string;
  connectorName: string;
  provider: string;
  mode: string;
  total: number;
  drifted: number;
  errors: number;
  notConfigured: boolean;
  listings: ListingRecap[];
}

const DOMAIN_LABELS: Record<string, string> = {
  content: "Content",
  price: "Price",
  inventory: "Inventory",
  variants: "Variants",
  media: "Media",
  publication: "Publication",
};

const DOMAIN_COLORS: Record<string, string> = {
  content: "bg-slate-100 text-slate-600",
  price: "bg-amber-100 text-amber-700",
  inventory: "bg-emerald-100 text-emerald-700",
  variants: "bg-slate-100 text-slate-600",
  media: "bg-slate-100 text-slate-600",
  publication: "bg-red-100 text-red-700",
};

export default function ReconciliationCentre() {
  const { org } = useAuth();
  const navigate = useNavigate();
  const [selectedConnector, setSelectedConnector] = useState<string>("all");
  const [selectedDomain, setSelectedDomain] = useState<string>("all");
  const [selectedMode, setSelectedMode] = useState<string>("all");
  const [detailListing, setDetailListing] = useState<ListingRecap | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["reconciliation-centre", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [connectors, ebayRec, bcRec, listings] = await Promise.all([
        supabase.from("connectors").select("id, name, provider, enabled").eq("org_id", org!.id).order("name"),
        supabase
          .from("ebay_provider_operations")
          .select("id, listing_id, connector_id, operation_type, current_state, error_class, error_message, created_at, updated_at")
          .eq("org_id", org!.id)
          .eq("operation_type", "reconciliation")
          .order("updated_at", { ascending: false })
          .limit(500),
        supabase
          .from("bigcommerce_provider_operations")
          .select("id, listing_id, connector_id, operation_type, current_state, error_class, error_message, created_at, updated_at")
          .eq("org_id", org!.id)
          .eq("operation_type", "reconciliation")
          .order("updated_at", { ascending: false })
          .limit(500),
        supabase
          .from("channel_listings")
          .select("id, title, connector_id, sync_status, last_sync_at, dirty_domains, provider_config")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false })
          .limit(500),
      ]);

      const connectorRows = (connectors.data ?? []) as ConnectorRow[];
      const connectorMap: Record<string, ConnectorRow> = {};
      connectorRows.forEach((c) => (connectorMap[c.id] = c));

      // Build listing recap per connector
      const listingRows = (listings.data ?? []) as {
        id: string;
        title: string | null;
        connector_id: string | null;
        sync_status: string | null;
        last_sync_at: string | null;
        dirty_domains: string[] | null;
        provider_config: Record<string, unknown> | null;
      }[];

      // Get connector configs for mode
      const [ebayConfigs, bcConfigs] = await Promise.all([
        supabase.from("connector_ebay_config").select("connector_id, app_credentials_status, auth_status").eq("org_id", org!.id),
        supabase.from("connector_bigcommerce_config").select("connector_id, app_credentials_status, auth_status").eq("org_id", org!.id),
      ]);

      const modeByConnector: Record<string, string> = {};
      ((ebayConfigs.data ?? []) as { connector_id: string; app_credentials_status: string; auth_status: string }[]).forEach((c) => {
        if (c.app_credentials_status !== "configured") modeByConnector[c.connector_id] = "NOT_CONFIGURED";
        else if (c.auth_status !== "connected") modeByConnector[c.connector_id] = "DRY_RUN";
        else modeByConnector[c.connector_id] = "LIVE";
      });
      ((bcConfigs.data ?? []) as { connector_id: string; app_credentials_status: string; auth_status: string }[]).forEach((c) => {
        if (c.app_credentials_status !== "configured") modeByConnector[c.connector_id] = "NOT_CONFIGURED";
        else if (c.auth_status !== "connected") modeByConnector[c.connector_id] = "DRY_RUN";
        else modeByConnector[c.connector_id] = "LIVE";
      });

      const groups: Record<string, ProviderGroup> = {};
      for (const listing of listingRows) {
        if (!listing.connector_id) continue;
        const connector = connectorMap[listing.connector_id];
        if (!connector) continue;

        const groupKey = listing.connector_id;
        if (!groups[groupKey]) {
          const mode = modeByConnector[listing.connector_id] ?? "NOT_CONFIGURED";
          groups[groupKey] = {
            connectorId: listing.connector_id,
            connectorName: connector.name,
            provider: connector.provider ?? "unknown",
            mode,
            total: 0,
            drifted: 0,
            errors: 0,
            notConfigured: mode === "NOT_CONFIGURED",
            listings: [],
          };
        }

        const hasDrift = (listing.dirty_domains ?? []).length > 0 || listing.sync_status === "error";
        const hasError = listing.sync_status === "error";
        const driftDomains = listing.dirty_domains ?? [];

        groups[groupKey].total += 1;
        if (hasDrift) groups[groupKey].drifted += 1;
        if (hasError) groups[groupKey].errors += 1;

        groups[groupKey].listings.push({
          connectorId: listing.connector_id,
          connectorName: connector.name,
          provider: connector.provider ?? "unknown",
          listingId: listing.id,
          listingTitle: listing.title,
          mode: groups[groupKey].mode,
          status: listing.sync_status ?? "unknown",
          driftDomains,
          driftDetails: null,
          action_taken: null,
          lastRun: listing.last_sync_at,
        });
      }

      return {
        connectors: connectorRows,
        groups: Object.values(groups),
        totalListings: listingRows.length,
        totalDrifted: listingRows.filter((l) => (l.dirty_domains ?? []).length > 0 || l.sync_status === "error").length,
      };
    },
  });

  const filtered = (data?.groups ?? []).flatMap((g) =>
    g.listings.filter((l) => {
      if (selectedConnector !== "all" && l.connectorId !== selectedConnector) return false;
      if (selectedDomain !== "all" && !l.driftDomains.includes(selectedDomain)) return false;
      if (selectedMode !== "all" && l.mode !== selectedMode) return false;
      return true;
    }).map((l) => ({ ...l, groupMode: g.mode }))
  );

  const driftedCount = filtered.filter((l) => l.driftDomains.length > 0 || l.status === "error").length;
  const errorsCount = filtered.filter((l) => l.status === "error").length;

  const handleAction = async (listing: ListingRecap, action: "republish" | "acknowledge") => {
    if (action === "republish") {
      navigate(`/listings/${listing.listingId}`);
    } else if (action === "acknowledge") {
      await supabase.rpc("clear_listing_dirty_domains", {
        p_org_id: org!.id,
        p_listing_id: listing.listingId,
        p_domains: listing.driftDomains,
      });
      refetch();
    }
    setDetailListing(null);
  };

  return (
    <div>
      <PageHeader title="Marketplace Reconciliation" subtitle="Cross-marketplace drift detection and sync status across all connected stores">
      </PageHeader>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Total listings</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{data?.totalListings ?? "—"}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Needs sync</p>
          <p className={`text-2xl font-bold mt-1 ${driftedCount > 0 ? "text-amber-600" : "text-emerald-700"}`}>{driftedCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Errors</p>
          <p className={`text-2xl font-bold mt-1 ${errorsCount > 0 ? "text-red-600" : "text-slate-900"}`}>{errorsCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Connectors</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{data?.groups.length ?? "—"}</p>
        </div>
      </div>

      {/* Connector summaries */}
      {(data?.groups ?? []).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {(data?.groups ?? []).map((g) => (
            <div key={g.connectorId} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{g.connectorName}</span>
                  <span className="text-xs capitalize text-slate-400">{g.provider}</span>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  g.mode === "LIVE" ? "bg-emerald-100 text-emerald-700" :
                  g.mode === "DRY_RUN" ? "bg-amber-100 text-amber-700" :
                  "bg-red-100 text-red-700"
                }`}>{reconciliationModeLabel(g.mode)}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-400">Listings</p>
                  <p className="font-semibold text-slate-800">{g.total}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Needs sync</p>
                  <p className={`font-semibold ${g.drifted > 0 ? "text-amber-600" : "text-slate-800"}`}>{g.drifted}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Errors</p>
                  <p className={`font-semibold ${g.errors > 0 ? "text-red-600" : "text-slate-800"}`}>{g.errors}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters + detail table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-wrap gap-3">
          <select value={selectedConnector} onChange={(e) => setSelectedConnector(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
            <option value="all">All connectors</option>
            {(data?.connectors ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
            <option value="all">All domains</option>
            {Object.entries(DOMAIN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={selectedMode} onChange={(e) => setSelectedMode(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
            <option value="all">All modes</option>
            <option value="LIVE">Live</option>
            <option value="DRY_RUN">Test mode</option>
            <option value="NOT_CONFIGURED">Not configured</option>
          </select>
          <span className="text-xs text-slate-400 ml-auto self-center">{filtered.length} listings</span>
        </div>

        {isLoading ? (
          <div className="p-8"><TableSkeleton /></div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-500 mb-4">Couldn't load reconciliation data.</p>
            <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-check-double-line" title="All in sync" description="No listings need attention based on current filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Listing</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Connector</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Mode</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Drift domains</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last sync</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={`${l.listingId}-${l.connectorId}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/listings/${l.listingId}`)}
                        className="text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline text-left"
                      >
                        {l.listingTitle ?? "Untitled listing"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-700">{l.connectorName}</p>
                      <p className="text-xs text-slate-400 capitalize">{l.provider}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        l.mode === "LIVE" ? "bg-emerald-100 text-emerald-700" :
                        l.mode === "DRY_RUN" ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      }`}>{reconciliationModeLabel(l.mode)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {l.driftDomains.length === 0 ? (
                          <span className="text-xs text-slate-400">None</span>
                        ) : (
                          l.driftDomains.map((d) => (
                            <span key={d} className={`text-xs px-2 py-0.5 rounded-full font-medium ${DOMAIN_COLORS[d] ?? "bg-slate-100 text-slate-600"}`}>
                              {DOMAIN_LABELS[d] ?? d}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-400">{l.lastRun ? fmtDateTime(l.lastRun) : "Never"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {l.driftDomains.length > 0 && (
                          <button
                            onClick={() => setDetailListing(l)}
                            className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-medium rounded-lg whitespace-nowrap"
                          >
                            Review
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/listings/${l.listingId}`)}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-100 text-xs text-slate-400">
            Test mode shows internal state only — comparison against live provider data requires live credentials. Drift domains indicate which aspects of the listing are out of sync.
          </div>
        )}
      </div>

      <Modal
        open={!!detailListing}
        onClose={() => setDetailListing(null)}
        title={`Drift Details — ${detailListing?.listingTitle ?? "Listing"}`}
        footer={
          <>
            <button onClick={() => setDetailListing(null)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Close
            </button>
            {detailListing && detailListing.driftDomains.length > 0 && (
              <>
                <button
                  onClick={() => detailListing && handleAction(detailListing, "acknowledge")}
                  className="px-4 py-2 bg-white border border-amber-200 hover:bg-amber-50 text-amber-700 text-sm font-medium rounded-lg whitespace-nowrap"
                >
                  Acknowledge
                </button>
                <button
                  onClick={() => detailListing && handleAction(detailListing, "republish")}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap"
                >
                  Go to listing
                </button>
              </>
            )}
          </>
        }
      >
        {detailListing && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-slate-600">Connector:</span>
              <span className="text-sm text-slate-800">{detailListing.connectorName}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                detailListing.mode === "LIVE" ? "bg-emerald-100 text-emerald-700" :
                detailListing.mode === "DRY_RUN" ? "bg-amber-100 text-amber-700" :
                "bg-red-100 text-red-700"
              }`}>{reconciliationModeLabel(detailListing.mode)}</span>
            </div>

            {detailListing.mode !== "LIVE" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                <i className="ri-information-line mr-1" />
                This connector is in {reconciliationModeLabel(detailListing.mode)} mode. Drift comparison is based on internal intended state only — no live provider comparison is made until live mode is active.
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Drift domains:</p>
              <div className="flex flex-wrap gap-2">
                {detailListing.driftDomains.map((d) => (
                  <div key={d} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${DOMAIN_COLORS[d] ?? "bg-slate-50 border-slate-200"}`}>
                    <i className="ri-git-pull-request-line" />
                    <span className="font-medium">{DOMAIN_LABELS[d] ?? d}</span>
                    <span className="text-xs opacity-70">needs sync</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
              <p className="font-semibold text-slate-700">What do these actions mean?</p>
              <p><span className="font-medium">Go to listing</span> — open the listing detail page to trigger a republish or sync.</p>
              <p><span className="font-medium">Acknowledge</span> — mark the drift as reviewed, clearing the dirty flag. Use this if the change was intentional or already handled externally. Does NOT push data to the provider.</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}